import React, { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAppContext } from '../context/AppContext';
import { db } from '../utils/db';
import {
    calcGuiltFree,
    calcGoalMonthly,
    getDaysLeftInMonth,
    getCurrentMonthSpending,
    getLastMonthSpending,
    detectSpendingContext,
} from '../utils/guiltFree';
import {
    LEAK_BUCKETS,
    budgetCategoryKey,
    getLeakBucketSpending,
    projectBucketPace,
    formatINR,
} from '../utils/leakBuckets';
import { generateInsights } from '../utils/insights';
import { detectRecurring } from '../utils/categorization';
import { getMonthlyProgress, getProgressCopy } from '../utils/progress';
import { insightTagsForSnark, pickSnark, shouldShowSnarkToday } from '../data/snark';
import StatementDropzone from './StatementDropzone';
import PasswordModal from './PasswordModal';

/**
 * Insightful mid-month home — guilt-free story, leaks, recurring, goals.
 */
export default function HomeView({ onShare }) {
    const {
        allTransactions,
        isSampleMode,
        sampleBudgets,
        sampleIncome,
        sampleFixedExpenses,
        sampleGoals,
        handleFile,
        passwordRequest,
        setPasswordRequest,
        status,
        lastUploadStats,
        triggerSampleLocked,
        setActiveTab,
        setAppScreen,
        setSpendFocusCategory,
    } = useAppContext();

    const dbBudgets = useLiveQuery(() => db.budgets.toArray(), []) || [];
    const dbFixed = useLiveQuery(() => db.fixed_expenses.toArray(), []) || [];
    const dbGoals = useLiveQuery(() => db.goals.toArray(), []) || [];

    const budgets = isSampleMode ? (sampleBudgets || []) : dbBudgets;
    const fixedExpenses = isSampleMode ? (sampleFixedExpenses || []) : dbFixed;
    const goals = isSampleMode ? (sampleGoals || []) : dbGoals;

    const income = isSampleMode && sampleIncome
        ? sampleIncome
        : parseFloat(localStorage.getItem('spenderly_monthly_income')) || 0;
    const lumpFixed = parseFloat(localStorage.getItem('spenderly_monthly_fixed')) || 0;

    // Prefer itemized fixed expenses; fall back to Reality Check lump so math never feels empty
    const fixedForCalc = fixedExpenses.length > 0
        ? fixedExpenses
        : (lumpFixed > 0
            ? [{ amount: lumpFixed, frequency: 'monthly', lastPaidDate: new Date().toISOString() }]
            : []);

    // Auto-detect known subscriptions into fixed_expenses (free, local-only)
    useEffect(() => {
        if (isSampleMode || !allTransactions.length) return;
        let cancelled = false;
        (async () => {
            const recurring = detectRecurring(allTransactions).filter(r => r.isKnownSubscription);
            if (!recurring.length || cancelled) return;
            const existing = await db.fixed_expenses.toArray();
            const names = new Set(existing.map(e => (e.name || '').toLowerCase()));
            for (const r of recurring) {
                const name = r.merchant;
                if (names.has(name.toLowerCase())) continue;
                await db.fixed_expenses.add({
                    name,
                    amount: Math.round(r.avgAmount),
                    type: 'subscription',
                    frequency: 'monthly',
                    lastPaidDate: r.lastDate || new Date().toISOString().split('T')[0],
                    isAutoDetected: true,
                });
                names.add(name.toLowerCase());
            }
        })();
        return () => { cancelled = true; };
    }, [allTransactions, isSampleMode]);

    const monthData = useMemo(() => getCurrentMonthSpending(allTransactions), [allTransactions]);
    const lastMonthSpent = useMemo(() => getLastMonthSpending(allTransactions), [allTransactions]);
    const context = useMemo(() => detectSpendingContext(allTransactions), [allTransactions]);
    const bucketSpend = useMemo(() => getLeakBucketSpending(allTransactions), [allTransactions]);
    const insights = useMemo(() => generateInsights(allTransactions).slice(0, 3), [allTransactions]);
    const snarkAside = useMemo(() => {
        if (!allTransactions.length || !shouldShowSnarkToday()) return null;
        const top = insights[0];
        const tags = top ? insightTagsForSnark(top.id) : ['general'];
        const day = new Date();
        const seed = `home:${day.getFullYear()}-${day.getMonth()}-${day.getDate()}:${top?.id || 'none'}`;
        return pickSnark(seed, tags);
    }, [allTransactions.length, insights]);
    const recurring = useMemo(() => detectRecurring(allTransactions), [allTransactions]);
    const progress = useMemo(() => getMonthlyProgress(allTransactions, 6), [allTransactions]);
    const progressCopy = useMemo(() => getProgressCopy(progress), [progress]);

    const leakBudgetsForCalc = LEAK_BUCKETS.map(b => {
        const key = budgetCategoryKey(b.id);
        const found = budgets.find(x => x.category === key || x.category === b.label);
        return { limit: found?.limit ?? b.defaultLimit };
    });

    const { guiltFree, totalFixed, totalGoals, totalBudgets } = calcGuiltFree(
        income,
        fixedForCalc,
        goals,
        leakBudgetsForCalc
    );

    const spentSoFar = context.isMidMonth ? context.spentSoFar : 0;
    const remaining = Math.max(0, guiltFree - spentSoFar);
    const daysLeft = Math.max(1, context.daysLeft || getDaysLeftInMonth());
    const daily = Math.floor(remaining / daysLeft);

    const gauges = LEAK_BUCKETS.map(b => {
        const key = budgetCategoryKey(b.id);
        const budget = budgets.find(x => x.category === key || x.category === b.label);
        const limit = budget?.limit ?? b.defaultLimit;
        const spent = bucketSpend[b.id]?.spent || 0;
        const pace = projectBucketPace(spent, limit);
        return { ...b, limit, spent, pace };
    });

    const topCategories = Object.entries(monthData.byCategory || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

    const knownSubs = recurring.filter(r => r.isKnownSubscription).slice(0, 6);
    const subLock = knownSubs.reduce((s, r) => s + (r.avgAmount || 0), 0);

    const vsLastMonth = lastMonthSpent > 0
        ? Math.round(((monthData.totalSpent - lastMonthSpent) / lastMonthSpent) * 100)
        : null;

    const [showUpload, setShowUpload] = useState(false);

    const sourceStatus = useMemo(() => ({
        phonepe: allTransactions.some(t => t.source === 'phonepe'),
        gpay: allTransactions.some(t => t.source === 'gpay'),
        paytm: allTransactions.some(t => t.source === 'paytm'),
    }), [allTransactions]);

    const onFile = async (source, file) => {
        if (isSampleMode) {
            triggerSampleLocked();
            return;
        }
        try {
            await handleFile(source, file, false);
            setShowUpload(true); // keep open so they can add another app
        } catch { /* status in context */ }
    };

    const mergeBanner = lastUploadStats
        ? `+${lastUploadStats.added} new · ${lastUploadStats.updated} already saved${
            lastUploadStats.autoTagged > 0 ? ` · ${lastUploadStats.autoTagged} auto-tagged from your rules` : ''
        }`
        : null;

    const openInsight = (ins) => {
        if (!ins?.action) return;
        if (ins.action.category) setSpendFocusCategory(ins.action.category);
        if (ins.action.tab) setActiveTab(ins.action.tab);
    };

    const deltaCopy = (() => {
        if (!lastUploadStats?.merged) return null;
        const worst = gauges.find(g => !g.pace.onTrack) || gauges[0];
        if (!worst) return null;
        const { pace } = worst;
        return pace.onTrack
            ? `${worst.shortLabel}: on track`
            : `${worst.shortLabel}: may exceed by ~${formatINR(pace.exceedBy)}`;
    })();

    return (
        <div className="home-rich">
            <p className="leak-eyebrow">Safe to spend</p>

            <div className="home-daily">
                <span className="home-daily-num">{formatINR(daily)}</span>
                <span className="home-daily-unit">/ day</span>
            </div>
            <p className="leak-sub home-hero-line">
                {formatINR(remaining)} left
                {vsLastMonth !== null && (
                    <> · {vsLastMonth > 0 ? `+${vsLastMonth}%` : `${vsLastMonth}%`} vs last month</>
                )}
            </p>

            {/* Multi-app status — first-class */}
            <div className="source-status" aria-label="Which UPI apps are loaded">
                {[
                    { key: 'phonepe', label: 'PhonePe' },
                    { key: 'gpay', label: 'GPay' },
                    { key: 'paytm', label: 'Paytm' },
                ].map(s => (
                    <button
                        key={s.key}
                        type="button"
                        className={`source-status-chip ${sourceStatus[s.key] ? 'on' : 'off'}`}
                        onClick={() => setShowUpload(true)}
                    >
                        <span>{s.label}</span>
                        <span className="source-status-flag">{sourceStatus[s.key] ? '✓' : 'Add'}</span>
                    </button>
                ))}
            </div>

            {mergeBanner && (
                <p className="home-status-line">{mergeBanner}</p>
            )}

            {/* Money math breakdown */}
            <div className="home-breakdown">
                <div className="home-bd-row"><span>Income</span><strong>{formatINR(income)}</strong></div>
                <div className="home-bd-row muted"><span>− Fixed</span><strong>{formatINR(totalFixed)}</strong></div>
                <div className="home-bd-row muted"><span>− Goals</span><strong>{formatINR(totalGoals)}</strong></div>
                <div className="home-bd-row muted"><span>− Leak caps</span><strong>{formatINR(totalBudgets)}</strong></div>
                {context.isMidMonth && (
                    <div className="home-bd-row muted"><span>− Spent</span><strong>{formatINR(spentSoFar)}</strong></div>
                )}
                <div className="home-bd-row total"><span>Left</span><strong>{formatINR(remaining)}</strong></div>
            </div>

            {deltaCopy && <p className="home-status-line">{deltaCopy}</p>}

            <div className="home-cta-block">
                <button type="button" className="sp-btn-primary" onClick={() => setShowUpload(v => !v)}>
                    {showUpload ? 'Hide upload' : 'Add statement'}
                </button>
            </div>

            {showUpload && (
                <div className="home-reupload">
                    <StatementDropzone
                        compact
                        onFile={onFile}
                        statusText={status?.active ? status.text : ''}
                        loadedSources={sourceStatus}
                    />
                </div>
            )}

            {/* Cumulative progress */}
            <section className="home-section">
                <h2 className="home-section-title">{progressCopy.title}</h2>
                {progressCopy.body && <p className="home-section-sub">{progressCopy.body}</p>}
                {progress.months.length > 0 && (
                    <div className="progress-bars">
                        {progress.months.map(m => (
                            <div key={m.prefix} className="progress-bar-col">
                                <div className="progress-bar-track">
                                    <div
                                        className="progress-bar-fill"
                                        style={{ height: `${Math.max(8, Math.round((m.spent / progress.maxSpent) * 100))}%` }}
                                    />
                                </div>
                                <span className="progress-bar-label">{m.label.replace(/ \d{4}/, '')}</span>
                                <span className="progress-bar-amt">{formatINR(m.spent)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Insights */}
            {insights.length > 0 && (
                <section className="home-section">
                    <h2 className="home-section-title">Noticed</h2>
                    <ul className="home-insight-list">
                        {insights.map(ins => (
                            <li key={ins.id}>
                                {ins.action ? (
                                    <button type="button" className="home-insight-btn" onClick={() => openInsight(ins)}>
                                        <span className="home-insight-kicker">{ins.title}</span>
                                        <p>{ins.body}</p>
                                    </button>
                                ) : (
                                    <>
                                        <span className="home-insight-kicker">{ins.title}</span>
                                        <p>{ins.body}</p>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                    {snarkAside && (
                        <p className="home-snark-aside" aria-hidden="true">{snarkAside}</p>
                    )}
                </section>
            )}

            {/* Leak buckets */}
            <section className="home-section">
                <h2 className="home-section-title">Leak caps</h2>
                <div className="home-gauges">
                    {gauges.map(g => {
                        const pct = g.limit > 0 ? Math.min(100, Math.round((g.spent / g.limit) * 100)) : 0;
                        const over = g.spent > g.limit;
                        return (
                            <div key={g.id} className="home-gauge">
                                <div className="home-gauge-top">
                                    <span>{g.shortLabel || g.label}</span>
                                    <span>{formatINR(g.spent)} / {formatINR(g.limit)}</span>
                                </div>
                                <div className="home-gauge-track">
                                    <div
                                        className={`home-gauge-fill ${over ? 'over' : g.pace.onTrack ? '' : 'warn'}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                {!g.pace.onTrack && (
                                    <div className="home-gauge-pace">
                                        Pace → {formatINR(g.pace.projected)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Recurring / subscriptions */}
            {knownSubs.length > 0 && (
                <section className="home-section">
                    <h2 className="home-section-title">
                        Recurring · {formatINR(subLock)}/mo
                    </h2>
                    <ul className="home-recurring-list">
                        {knownSubs.map(r => (
                            <li key={r.merchant}>
                                <span>{r.merchant}</span>
                                <strong>{formatINR(r.avgAmount)}</strong>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Goals */}
            {goals.length > 0 && (
                <section className="home-section">
                    <h2 className="home-section-title">Goals</h2>
                    <ul className="home-goal-list">
                        {goals.map((g, i) => {
                            const monthly = calcGoalMonthly(g.targetAmount, g.savedSoFar, g.deadline);
                            const pct = g.targetAmount > 0
                                ? Math.min(100, Math.round(((g.savedSoFar || 0) / g.targetAmount) * 100))
                                : 0;
                            return (
                                <li key={g.id || i}>
                                    <div className="home-goal-top">
                                        <span>{g.name}</span>
                                        <strong>{formatINR(monthly)}/mo</strong>
                                    </div>
                                    <div className="home-gauge-track">
                                        <div className="home-gauge-fill" style={{ width: `${pct}%` }} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            {/* Category breakdown */}
            {topCategories.length > 0 && (
                <section className="home-section">
                    <h2 className="home-section-title">Top categories</h2>
                    <ul className="home-cat-list">
                        {topCategories.map(([cat, amt]) => {
                            const pct = monthData.totalSpent > 0
                                ? Math.round((amt / monthData.totalSpent) * 100)
                                : 0;
                            return (
                                <li key={cat}>
                                    <div className="home-cat-top">
                                        <span>{cat}</span>
                                        <strong>{formatINR(amt)} · {pct}%</strong>
                                    </div>
                                    <div className="home-gauge-track">
                                        <div className="home-gauge-fill" style={{ width: `${pct}%` }} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            <div className="home-secondary-actions">
                <button type="button" className="action-row" onClick={() => setAppScreen('targets')}>
                    <span className="action-row-text">
                        <strong>Leak limits</strong>
                    </span>
                    <span className="action-row-chevron" aria-hidden>›</span>
                </button>
                <button type="button" className="action-row" onClick={() => setActiveTab('activity')}>
                    <span className="action-row-text">
                        <strong>Fix categories</strong>
                    </span>
                    <span className="action-row-chevron" aria-hidden>›</span>
                </button>
                <button type="button" className="action-row" onClick={() => setActiveTab('settings')}>
                    <span className="action-row-text">
                        <strong>Money setup</strong>
                    </span>
                    <span className="action-row-chevron" aria-hidden>›</span>
                </button>
                {onShare && (
                    <button type="button" className="btn-pill home-share-pill" onClick={onShare}>
                        Share
                    </button>
                )}
            </div>

            {passwordRequest && (
                <PasswordModal
                    title={passwordRequest.reason === 1 ? 'Locked PDF' : 'Wrong password'}
                    message="Processed only on this phone."
                    onSubmit={(pwd) => {
                        passwordRequest.callback(pwd);
                        setPasswordRequest(null);
                    }}
                    onCancel={() => {
                        passwordRequest.callback(new Error('Password cancelled'));
                        setPasswordRequest(null);
                    }}
                />
            )}
        </div>
    );
}
